
import React, { useEffect } from "react";
import { useProgramCreation } from "@/contexts/ProgramCreationContext";
import ClientSelection from "@/components/programs/ClientSelection";
import ClientDetails from "@/components/programs/ClientDetails";
import ProgramForm from "@/components/programs/ProgramForm";
import BeginnerTierQuestions from "@/components/programs/BeginnerTierQuestions";
import IntermediateTierQuestions from "@/components/programs/IntermediateTierQuestions";
import AdvancedTierQuestions from "@/components/programs/AdvancedTierQuestions";
import AthleteTierQuestions from "@/components/programs/AthleteTierQuestions";
import PreferencesCustomizationsForm from "@/components/programs/PreferencesCustomizationsForm";
import ProgramReviewForm from "@/components/programs/ProgramReviewForm";
import { TierFormData } from "@/contexts/program-creation/types";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";

export default function ProgramCreationStepContent() {
  const { 
    step, 
    clients, 
    isLoading, 
    selectedClient, 
    isSaving,
    handleNextStep,
    handlePreviousStep,
    updateClientDetails,
    handleSaveProgram,
    setSelectedClient
  } = useProgramCreation();
  
  const { checkCanAddProgramToClient } = usePermissionCheck();

  // Don't automatically check permissions on component mount
  // This will now only be checked when proceeding to the final step

  const handleTierSubmit = (data: any) => {
    // Save tier-specific data to localStorage
    localStorage.setItem('tierData', JSON.stringify(data));
    handleNextStep();
  };

  const renderTierSpecificForm = () => {
    const programDataStr = localStorage.getItem('programData');
    if (!programDataStr) return null;
    
    const programData = JSON.parse(programDataStr);
    const tier = programData.tier;
    
    switch (tier) {
      case "Beginner":
        return (
          <BeginnerTierQuestions 
            onSubmit={handleTierSubmit} 
            onBack={handlePreviousStep}
            isSaving={isSaving}
          />
        );
      case "Intermediate":
        return (
          <IntermediateTierQuestions 
            onSubmit={handleTierSubmit} 
            onBack={handlePreviousStep}
            isSaving={isSaving}
          />
        );
      case "Advanced":
        return (
          <AdvancedTierQuestions 
            onSubmit={handleTierSubmit} 
            onBack={handlePreviousStep}
            isSaving={isSaving}
          />
        );
      case "Athlete":
        return (
          <AthleteTierQuestions 
            onSubmit={handleTierSubmit} 
            onBack={handlePreviousStep}
            isSaving={isSaving}
          />
        );
      default:
        return null;
    }
  };

  const handleClientSelect = async (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      // Only do a basic check here without showing errors
      const canAddProgram = await checkCanAddProgramToClient(client.id);
      if (!canAddProgram) {
        // We won't show an error here, as we want to still let them select the client
        // The error will show on the final step when they try to create the program
        console.log("Client has reached program limit, but we'll let them proceed with selection");
      }
      setSelectedClient(client);
    }
  };

  const handlePreferencesSubmit = (data: any) => {
    localStorage.setItem('preferencesData', JSON.stringify(data));
    handleNextStep();
  };

  return (
    <div className="animate-fade-in space-y-6">
      {step === 1 && (
        <>
          <ClientSelection 
            clients={clients} 
            isLoading={isLoading} 
            onClientSelect={handleClientSelect} 
          />

          {selectedClient && (
            <ClientDetails 
              client={selectedClient}
              onUpdateClientDetails={updateClientDetails}
              isSaving={isSaving}
              onContinue={handleNextStep}
            />
          )}
        </>
      )}

      {step === 2 && (
        <ProgramForm 
          onBack={handlePreviousStep}
          onContinue={handleNextStep}
        />
      )}

      {step === 3 && renderTierSpecificForm()}

      {step === 4 && (
        <PreferencesCustomizationsForm
          onSubmit={handlePreferencesSubmit}
          onBack={handlePreviousStep}
          isSaving={isSaving}
        />
      )}

      {step === 5 && (
        <ProgramReviewForm
          onSubmit={handleSaveProgram}
          onBack={handlePreviousStep}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}
